import { forwardRef, useId } from 'react'
import { cx } from './utils.js'

// Field primitives (migrated off Tailwind in Wave 47q).
// Input / Textarea / Select share the .studio-field-control surface
// with `.textarea` / `.select` modifiers.

function FieldFrame({ id, label, helper, error, children, className }) {
  const helperId = helper || error ? `${id}-description` : undefined
  return (
    <div className={cx('studio-field', className)}>
      {label ? (
        <label htmlFor={id} className="studio-field-label">
          {label}
        </label>
      ) : null}
      {children({ id, describedBy: helperId })}
      {helper || error ? (
        <p
          id={helperId}
          className={cx('studio-field-helper', error ? 'error' : '')}
        >
          {error || helper}
        </p>
      ) : null}
    </div>
  )
}

export const Input = forwardRef(function Input(
  { id, label, helper, error, className, inputClassName, ...props },
  ref
) {
  const generatedId = useId()
  const fieldId = id || `studio-input-${generatedId}`
  return (
    <FieldFrame
      id={fieldId}
      label={label}
      helper={helper}
      error={error}
      className={className}
    >
      {({ id: controlId, describedBy }) => (
        <input
          ref={ref}
          id={controlId}
          aria-describedby={describedBy}
          className={cx('studio-field-control', inputClassName)}
          {...props}
        />
      )}
    </FieldFrame>
  )
})

export const Textarea = forwardRef(function Textarea(
  { id, label, helper, error, rows = 4, className, inputClassName, ...props },
  ref
) {
  const generatedId = useId()
  const fieldId = id || `studio-textarea-${generatedId}`
  return (
    <FieldFrame
      id={fieldId}
      label={label}
      helper={helper}
      error={error}
      className={className}
    >
      {({ id: controlId, describedBy }) => (
        <textarea
          ref={ref}
          id={controlId}
          rows={rows}
          aria-describedby={describedBy}
          className={cx('studio-field-control', 'textarea', inputClassName)}
          {...props}
        />
      )}
    </FieldFrame>
  )
})

export const Select = forwardRef(function Select(
  { id, label, helper, error, className, inputClassName, children, ...props },
  ref
) {
  const generatedId = useId()
  const fieldId = id || `studio-select-${generatedId}`
  return (
    <FieldFrame
      id={fieldId}
      label={label}
      helper={helper}
      error={error}
      className={className}
    >
      {({ id: controlId, describedBy }) => (
        <select
          ref={ref}
          id={controlId}
          aria-describedby={describedBy}
          className={cx('studio-field-control', 'select', inputClassName)}
          {...props}
        >
          {children}
        </select>
      )}
    </FieldFrame>
  )
})
